import React, { useContext, useMemo, useEffect, useState, useRef, useCallback } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Sun, Moon, Monitor, Gauge } from "lucide-react";
import { AuthContext } from "@/App";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Stock Center shell — Rockpecker Drilling warehouse portal. A sibling app to
// the ERP <Layout>: it deliberately does NOT load the ERP data contexts, but it
// wears a faithful match of the RockLink header (Rockpecker logo + RockLink
// wordmark + the frosted user pill) so it reads as ONE product. ERP-only header
// tools (global search, mentions, stage filters, sync pill) are intentionally
// omitted — they don't apply to a warehouse.

const LOGO_URL = "/logo-rockpecker-drilling.webp";

// Self-contained theme controller — mirrors the ERP's ThemeContext machinery
// (light/auto/dark + flat mode) so warehouse-only users, who never mount MainApp,
// get the SAME appearance controls. Persists to the shared rl_theme / rl_flat
// keys and applies to <html> exactly like the ERP, including data-flat.
function applyThemePref(pref) {
  let dark = false;
  if (pref === "dark") dark = true;
  else if (pref === "system") { try { dark = window.matchMedia("(prefers-color-scheme: dark)").matches; } catch {} }
  // Preserve any non-theme classes; only toggle light/dark.
  const el = document.documentElement;
  el.classList.remove("light", "dark");
  el.classList.add(dark ? "dark" : "light");
}
function useStockTheme() {
  const [themePref, setPref] = useState(() => {
    try { const s = localStorage.getItem("rl_theme"); return ["light", "dark", "system"].includes(s) ? s : "system"; }
    catch { return "system"; }
  });
  const [flatMode, setFlat] = useState(() => {
    try { return localStorage.getItem("rl_flat") === "1"; } catch { return false; }
  });
  const prefRef = useRef(themePref);
  useEffect(() => { prefRef.current = themePref; }, [themePref]);

  useEffect(() => { applyThemePref(themePref); }, [themePref]);
  useEffect(() => {
    if (flatMode) document.documentElement.setAttribute("data-flat", "1");
    else document.documentElement.removeAttribute("data-flat");
  }, [flatMode]);
  // "Auto" follows the OS live.
  useEffect(() => {
    if (themePref !== "system") return;
    let mq; const apply = () => applyThemePref("system");
    try { mq = window.matchMedia("(prefers-color-scheme: dark)"); mq.addEventListener?.("change", apply); } catch {}
    return () => { try { mq?.removeEventListener?.("change", apply); } catch {} };
  }, [themePref]);

  const setThemePref = useCallback((p) => { setPref(p); try { localStorage.setItem("rl_theme", p); } catch {} }, []);
  const cycleTheme = useCallback(() => {
    const order = ["light", "system", "dark"];
    const next = order[(order.indexOf(prefRef.current) + 1) % order.length];
    setThemePref(next); return next;
  }, [setThemePref]);
  const toggleFlat = useCallback(() => {
    setFlat((v) => { const next = !v; try { localStorage.setItem("rl_flat", next ? "1" : "0"); } catch {} return next; });
  }, []);
  return { themePref, setThemePref, cycleTheme, flatMode, toggleFlat };
}

const THEME_OPTIONS = [
  { key: "light",  icon: Sun,     label: "Light" },
  { key: "system", icon: Monitor, label: "Auto"  },
  { key: "dark",   icon: Moon,    label: "Dark"  },
];

// Theme slider (Light / Auto / Dark) — same carved-capsule + lifted-thumb
// vocabulary as the ERP user menu. Lives outside DropdownMenuItem so picking a
// theme doesn't close the menu.
function ThemeSlider({ themePref, setThemePref }) {
  return (
    <div className="px-3 pt-2 pb-1.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1.5">Appearance</p>
      <div className="rl-carved flex gap-0.5 p-0.5 rounded-full bg-[hsl(var(--muted))]/50" role="radiogroup" aria-label="Theme" data-testid="sc-theme-slider">
        {THEME_OPTIONS.map(({ key, icon: Icon, label }) => (
          <button key={key} type="button" role="radio" aria-checked={themePref === key}
            onClick={() => setThemePref(key)} data-testid={`sc-theme-${key}-btn`}
            className={`flex-1 flex items-center justify-center gap-1 h-6 rounded-full text-[11px] font-medium transition-colors ${
              themePref === key
                ? "rl-thumb bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))]"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            }`}>
            <Icon className="w-3 h-3" />{label}
          </button>
        ))}
      </div>
      {themePref === "system" && (
        <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1.5 text-center whitespace-nowrap">Follows your device setting</p>
      )}
    </div>
  );
}

function FlatModeRow({ flatMode, toggleFlat }) {
  return (
    <div className="px-3 pt-1.5 pb-2" data-testid="sc-flat-mode-row">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm whitespace-nowrap text-[hsl(var(--foreground))]">
          <Gauge className="w-4 h-4 shrink-0 text-[hsl(var(--muted-foreground))]" /> Reduce visual effects
        </span>
        <Switch checked={flatMode} onCheckedChange={toggleFlat} aria-label="Reduce visual effects" data-testid="sc-flat-mode-switch" />
      </div>
      <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1 pl-6">Snappier on older devices</p>
    </div>
  );
}

const ROLE_BADGE = {
  Admin: "bg-orange-500/15 text-orange-500 border-orange-500/30",
  "Stock Center": "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
};

export default function StockLayout() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { themePref, setThemePref, cycleTheme, flatMode, toggleFlat } = useStockTheme();

  const initials = useMemo(() => {
    const n = (user?.name || user?.email || "?").trim();
    const parts = n.split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || n[0]?.toUpperCase() || "?";
  }, [user]);
  const firstName = (user?.name || user?.email || "").split(/\s+/)[0];

  // Dual-access users have an ERP footing too; warehouse-only (role "Stock
  // Center") should NOT see a way back into the ERP.
  const isDualAccess = user?.role && user.role !== "Stock Center";

  useEffect(() => {
    const prev = document.title;
    document.title = "Stock Center — Rockpecker Drilling";
    return () => { document.title = prev; };
  }, []);

  // Keyboard shortcut: N cycles the theme (matches the ERP). Ignored while the
  // user is typing in a field.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (e.key === "n" || e.key === "N") { e.preventDefault(); cycleTheme(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [cycleTheme]);

  return (
    <div className="min-h-screen rl-canvas" data-testid="stock-center-layout">
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]/95 backdrop-blur">
        <div className="max-w-[1440px] mx-auto px-4 h-14 flex items-center justify-between">
          {/* Brand lockup — matches the ERP: Rockpecker mark · RockLink wordmark
              · the division sub-label in place of the version badge. */}
          <div className="flex items-center gap-3" data-testid="sc-brand">
            <img src={LOGO_URL} alt="Rockpecker Drilling" className="h-8 w-auto object-contain object-left select-none" style={{ maxWidth: "120px" }} draggable={false} />
            <div className="hidden sm:flex flex-col pl-3 border-l border-[hsl(var(--border))]">
              <div className="flex items-center gap-2">
                <span className="text-lg font-extrabold tracking-tight leading-none" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.01em" }}>
                  <span style={{ color: "#ef4444" }}>Rock</span><span style={{ color: "#f97316" }}>Link</span>
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-[#0F6E56]/12 text-[#0F6E56] dark:text-[#5DCAA5] border border-[#0F6E56]/25">
                  Stock Center
                </span>
              </div>
              <span className="text-[10px] text-[hsl(var(--muted-foreground))] leading-tight mt-0.5">Rockpecker Drilling · Global warehouses</span>
            </div>
          </div>

          {/* Right cluster — trimmed: ERP switch (dual-access) + user menu. */}
          <div className="flex items-center gap-2">
            {isDualAccess && (
              <button
                onClick={() => navigate("/")}
                className="rl-frost-btn flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                data-testid="switch-to-erp"
                title="Back to the main RockLink ERP"
              >
                RockLink ERP <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-1.5 h-8 pl-1 pr-2.5 rounded-full text-[hsl(var(--muted-foreground))] transition-[colors,box-shadow] duration-150 ${
                    menuOpen ? "bg-zinc-200 dark:bg-zinc-800 [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]" : "rl-frost-btn"
                  }`}
                  data-testid="sc-user-menu" aria-label="User menu"
                >
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-[#ef4444] to-[#f97316] text-white text-[10px] font-bold shrink-0">{initials}</span>
                  <span className="hidden sm:inline text-sm font-medium text-[hsl(var(--foreground))]">{firstName}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${menuOpen ? "rotate-180" : ""}`} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 rl-frost">
                <div className="px-3 py-2.5">
                  <p className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">{user?.name || user?.email}</p>
                  <span className={`mt-1 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${ROLE_BADGE[user?.role] || "bg-slate-500/15 text-slate-500 border-slate-500/30"}`}>
                    {user?.role}
                  </span>
                </div>
                <DropdownMenuSeparator />
                {/* Appearance — same Light/Auto/Dark slider + reduce-visual-effects
                    switch as the ERP user menu. Outside DropdownMenuItem so the
                    menu stays open while toggling. */}
                <ThemeSlider themePref={themePref} setThemePref={setThemePref} />
                <FlatModeRow flatMode={flatMode} toggleFlat={toggleFlat} />
                <p className="px-3 pb-1 text-[10px] text-[hsl(var(--muted-foreground))]/70">Press <kbd className="font-mono-data">N</kbd> to cycle theme</p>
                {isDualAccess && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/")}><ArrowRight className="w-4 h-4 mr-2" /> Go to RockLink ERP</DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-[hsl(var(--destructive))]">Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-4 py-5">
        <Outlet />
      </main>
    </div>
  );
}
